import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { useTrackAgentWorkspaceModified } from '@air/analytics';

import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunRecord,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { buildWorkspaceAgentToolCallById } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentToolCallById';
import { getWorkspaceAgentCurrentViewInvalidationTargets } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentCurrentViewInvalidationTargets';
import {
  getWorkspaceAgentFinishedRunUserMessageId,
  getWorkspaceAgentRunSegmentMessages,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentRunSegment';
import { invalidateWorkspaceAgentBoardNavigationQueries } from '~/components/v2/components/workspace/workspace-agent-panel/utils/invalidateWorkspaceAgentBoardNavigationQueries';
import { invalidateWorkspaceAgentModifiedAssetQueries } from '~/components/v2/components/workspace/workspace-agent-panel/utils/invalidateWorkspaceAgentModifiedAssetQueries';
import { readWorkspaceAgentMutationAnalyticsFromToolResult } from '~/components/v2/components/workspace/workspace-agent-panel/utils/readWorkspaceAgentMutationAnalyticsFromToolResult';
import { resolveWorkspaceAgentMutatingToolName } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentMutatingToolName';
import { shouldFlushWorkspaceAgentCacheInvalidationOnToolResult } from '~/components/v2/components/workspace/workspace-agent-panel/utils/shouldFlushWorkspaceAgentCacheInvalidationOnToolResult';
import { workspaceAgentMessageShouldInvalidateCaches } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageShouldInvalidateCaches';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { centralizedClipAssetIdSelector, centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { isKanbanViewSelector } from '~/store/configViews/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export type UsePrivateWorkspaceAgentModifiedAssetCacheInvalidationParams = {
  getActiveSessionId: () => string | null;
  getMessagesSnapshot: () => WorkspaceAgentMessage[];
  getRunsById: () => Record<string, WorkspaceAgentRunRecord>;
  onMessageAdded: (message: WorkspaceAgentMessage) => void;
  onRunFinished: (event: WorkspaceAgentRunFinishedEvent) => void;
};

/**
 * Invalidates the current view after workspace agent mutations and fires `agent_workspace_modified`
 * for successful mutating toolResults on live WSS delivery.
 *
 * Backend `mutated` on each `toolResult` records that a mayMutate tool wrote workspace data.
 * Most tools batch cache refresh until `Run.finished` for the open session; `bulk_variants` also
 * flushes on each mutating `toolResult` so new variants can appear between long batches. A macrotask
 * backup on run end scans only that run's segment when finish lands before pending is set.
 *
 * Open session: full visual-context invalidation. Other workspace sessions: board-navigation
 * query keys only (WSS delivers every session's `toolResult`s).
 */
export const usePrivateWorkspaceAgentModifiedAssetCacheInvalidation = ({
  getActiveSessionId,
  getMessagesSnapshot,
  getRunsById,
  onMessageAdded,
  onRunFinished,
}: UsePrivateWorkspaceAgentModifiedAssetCacheInvalidationParams) => {
  const queryClient = useQueryClient();
  const store = useAirStore();
  const { trackAgentWorkspaceModified } = useTrackAgentWorkspaceModified();
  const onMessageAddedRef = useRef(onMessageAdded);
  const onRunFinishedRef = useRef(onRunFinished);
  const pendingMutatingSessionIdRef = useRef<string | null>(null);
  const flushTimeoutIdRef = useRef<number | null>(null);
  const trackedMutationToolResultIdsRef = useRef(new Set<string>());
  const lastTrackedSessionIdRef = useRef<string | null>(null);

  const flushBoardNavigationInvalidation = useCallback(() => {
    invalidateWorkspaceAgentBoardNavigationQueries({ queryClient });
  }, [queryClient]);

  const flushCurrentViewInvalidation = useCallback(() => {
    const state = store.getState();

    invalidateWorkspaceAgentModifiedAssetQueries({
      currentView: getWorkspaceAgentCurrentViewInvalidationTargets(window.location.pathname, {
        boardId: centralizedBoardIdSelector(state),
        clipAssetId: centralizedClipAssetIdSelector(state),
        clipId: centralizedClipIdSelector(state),
        isKanbanView: isKanbanViewSelector(state),
      }),
      queryClient,
    });
  }, [queryClient, store]);

  onMessageAddedRef.current = onMessageAdded;
  onRunFinishedRef.current = onRunFinished;

  useEffect(
    () => () => {
      if (flushTimeoutIdRef.current !== null) {
        window.clearTimeout(flushTimeoutIdRef.current);
      }
    },
    [],
  );

  const handleMessageAdded = useCallback(
    (message: WorkspaceAgentMessage) => {
      onMessageAddedRef.current(message);

      const activeSessionId = getActiveSessionId();

      if (message.role !== 'toolResult') {
        return;
      }

      if (message.sessionId !== activeSessionId) {
        if (workspaceAgentMessageShouldInvalidateCaches(message)) {
          flushBoardNavigationInvalidation();
        }

        return;
      }

      if (lastTrackedSessionIdRef.current !== activeSessionId) {
        trackedMutationToolResultIdsRef.current.clear();
        lastTrackedSessionIdRef.current = activeSessionId;
      }

      const messages = getMessagesSnapshot();

      if (!trackedMutationToolResultIdsRef.current.has(message.id)) {
        const analyticsPayload = readWorkspaceAgentMutationAnalyticsFromToolResult({
          messages,
          toolResultMessage: message,
        });

        if (analyticsPayload) {
          trackedMutationToolResultIdsRef.current.add(message.id);
          trackAgentWorkspaceModified(analyticsPayload);
        }
      }

      if (workspaceAgentMessageShouldInvalidateCaches(message)) {
        pendingMutatingSessionIdRef.current = message.sessionId;

        const toolName = resolveWorkspaceAgentMutatingToolName(message, buildWorkspaceAgentToolCallById(messages));

        if (shouldFlushWorkspaceAgentCacheInvalidationOnToolResult(toolName)) {
          flushCurrentViewInvalidation();
        }
      }
    },
    [
      flushBoardNavigationInvalidation,
      flushCurrentViewInvalidation,
      getActiveSessionId,
      getMessagesSnapshot,
      trackAgentWorkspaceModified,
    ],
  );

  const handleRunFinished = useCallback(
    (event: WorkspaceAgentRunFinishedEvent) => {
      onRunFinishedRef.current(event);

      if (event.sessionId !== getActiveSessionId()) {
        return;
      }

      if (flushTimeoutIdRef.current !== null) {
        window.clearTimeout(flushTimeoutIdRef.current);
        flushTimeoutIdRef.current = null;
      }

      flushTimeoutIdRef.current = window.setTimeout(() => {
        flushTimeoutIdRef.current = null;

        if (event.sessionId !== getActiveSessionId()) {
          pendingMutatingSessionIdRef.current = null;
          return;
        }

        const pendingMutatingSessionId = pendingMutatingSessionIdRef.current;
        pendingMutatingSessionIdRef.current = null;
        const messages = getMessagesSnapshot();
        const userMessageId = getWorkspaceAgentFinishedRunUserMessageId({
          messages,
          runId: event.runId,
          runsById: getRunsById(),
        });
        const runSegmentMessages = userMessageId ? getWorkspaceAgentRunSegmentMessages(messages, userMessageId) : [];
        const shouldInvalidate =
          pendingMutatingSessionId === event.sessionId ||
          runSegmentMessages.some(workspaceAgentMessageShouldInvalidateCaches);

        if (!shouldInvalidate) {
          return;
        }

        flushCurrentViewInvalidation();
      }, 0);
    },
    [flushCurrentViewInvalidation, getActiveSessionId, getMessagesSnapshot, getRunsById],
  );

  return { handleMessageAdded, handleRunFinished };
};
